import { buildScoreValue, getScoreInputConfig } from '@/components/custom/score-log-form-utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useScoresApi } from '@/hooks/useScoresApi';
import { useSubmissionLock } from '@/hooks/useSubmissionLock';
import { publishLeaderboardUpdate } from '@/lib/leaderboard-sync';
import { cn } from '@/lib/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, Loader2, Trophy } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ScoreModalBody, ScoreModalFooter } from './ScoreModalLayout';

interface ScoreLogFormProps {
  workoutId: string;
  workoutName: string;
  scoringType: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  onPendingChange?: (isPending: boolean) => void;
}

interface ScoreSubmissionPayload {
  workout_id: string;
  value: number | string;
  division: string;
  notes?: string;
}

const divisions = [
  { value: 'rx', label: 'Rx' },
  { value: 'scaled', label: 'Scaled' },
  { value: 'adaptive', label: 'Adaptive' },
] as const;

export function ScoreLogForm({
  workoutId,
  workoutName,
  scoringType,
  onSuccess,
  onCancel,
  onPendingChange,
}: ScoreLogFormProps) {
  const scoresApi = useScoresApi();
  const queryClient = useQueryClient();
  const [division, setDivision] = useState<string>('rx');
  const [notes, setNotes] = useState('');
  const [result, setResult] = useState<{ position: number; total: number } | null>(null);

  // Scoring-type-specific state
  const [timeMinutes, setTimeMinutes] = useState('');
  const [timeSeconds, setTimeSeconds] = useState('');
  const [rounds, setRounds] = useState('');
  const [reps, setReps] = useState('');
  const [numericValue, setNumericValue] = useState('');
  const inputConfig = getScoreInputConfig(scoringType);
  const submission = useSubmissionLock<ScoreSubmissionPayload>();

  useEffect(() => {
    onPendingChange?.(submission.isLocked);
  }, [onPendingChange, submission.isLocked]);

  const submitMutation = useMutation({
    mutationFn: async (attempt: { payload: ScoreSubmissionPayload; idempotencyKey: string }) => {
      const response = await scoresApi.submitScore(attempt.payload, attempt.idempotencyKey);
      if (!response.success) throw new Error(response.error || 'Failed to log score');
      return response;
    },
    onSuccess: (response) => {
      if (response.success && response.data) {
        submission.reset();
        setResult({
          position: response.data.position,
          total: response.data.total_entries,
        });
        toast.success(
          `Score logged! You placed ${response.data.position} out of ${response.data.total_entries}`
        );
        queryClient.invalidateQueries({
          predicate: ({ queryKey }) =>
            queryKey.some((key) => typeof key === 'string' && key.includes('leaderboard')),
        });
        publishLeaderboardUpdate(workoutId);
        onSuccess?.();
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to log score');
      submission.release();
    },
  });

  const handleSubmit = () => {
    const value = buildScoreValue(scoringType, {
      timeMinutes,
      timeSeconds,
      rounds,
      reps,
      numericValue,
    });
    const payload: ScoreSubmissionPayload = {
      workout_id: workoutId,
      value,
      division,
      notes: notes || undefined,
    };
    const attempt = submission.begin(payload);
    if (!attempt) return;
    submitMutation.mutate(attempt);
  };

  if (result) {
    return (
      <Card className="border-primary/30">
        <CardContent className="flex flex-col items-center justify-center py-8 text-center">
          <Trophy className="h-12 w-12 text-primary mb-3" />
          <h3 className="text-lg font-bold">Score Logged!</h3>
          <p className="text-muted-foreground mt-1">
            You placed <strong className="text-primary">{result.position}</strong> out of{' '}
            {result.total}
          </p>
          <Button variant="outline" className="mt-4" onClick={onCancel}>
            Close
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="flex max-h-[calc(100dvh-2rem)] min-h-0 flex-col overflow-hidden">
      <CardHeader className="shrink-0 pb-3">
        <CardTitle className="text-base">Log Score: {workoutName}</CardTitle>
      </CardHeader>
      <CardContent className="flex min-h-0 flex-1 flex-col p-0">
        <ScoreModalBody className="space-y-4 px-6 pb-4">
          {/* Division Selector */}
          <div>
            <Label className="text-xs text-muted-foreground mb-2 block">Division</Label>
            <div className="flex gap-1">
              {divisions.map((d) => (
                <Button
                  key={d.value}
                  type="button"
                  variant={division === d.value ? 'default' : 'outline'}
                  size="sm"
                  className={cn(
                    'flex-1',
                    division === d.value && 'bg-primary text-primary-foreground'
                  )}
                  onClick={() => setDivision(d.value)}
                >
                  {d.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Score Input */}
          <div>
            <Label className="text-xs text-muted-foreground mb-2 block">{inputConfig.label}</Label>

            {scoringType === 'time' && (
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  placeholder="Min"
                  value={timeMinutes}
                  onChange={(e) => setTimeMinutes(e.target.value)}
                  className="w-20 text-center"
                />
                <span className="text-lg font-bold">:</span>
                <Input
                  type="number"
                  min="0"
                  max="59"
                  placeholder="Sec"
                  value={timeSeconds}
                  onChange={(e) => setTimeSeconds(e.target.value)}
                  className="w-20 text-center"
                />
              </div>
            )}

            {scoringType === 'rounds_reps' && (
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  placeholder="Rounds"
                  value={rounds}
                  onChange={(e) => setRounds(e.target.value)}
                  className="w-24"
                />
                <span className="text-lg font-bold">+</span>
                <Input
                  type="number"
                  min="0"
                  placeholder="Reps"
                  value={reps}
                  onChange={(e) => setReps(e.target.value)}
                  className="w-24"
                />
              </div>
            )}

            {inputConfig.numeric && (
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  placeholder="0"
                  value={numericValue}
                  onChange={(e) => setNumericValue(e.target.value)}
                />
                {inputConfig.unit && (
                  <span className="text-sm text-muted-foreground">{inputConfig.unit}</span>
                )}
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <Label className="text-xs text-muted-foreground mb-2 block">Notes (optional)</Label>
            <Input
              placeholder="How did it go?"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={500}
            />
          </div>
        </ScoreModalBody>

        {/* Actions stay visible while the fields scroll above them. */}
        <ScoreModalFooter className="flex gap-2">
          <Button className="flex-1" onClick={handleSubmit} disabled={submission.isLocked}>
            {submission.isLocked ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle className="h-4 w-4 mr-2" />
            )}
            Log Score
          </Button>
          {onCancel && (
            <Button variant="outline" onClick={onCancel} disabled={submission.isLocked}>
              Cancel
            </Button>
          )}
        </ScoreModalFooter>
      </CardContent>
    </Card>
  );
}
